import Swal from "sweetalert2";

/**
 * Types
 */
export type SedeStatusRow = {
  sede: string;
  existencia: number;
  vmd: number;
  ultima_compra_fecha: string | null;
  ultima_compra_cantidad: number;
  ultima_venta_fecha: string | null;
  ultima_venta_cantidad: number;
};

type AnyRow = Record<string, any>;

/**
 * Helpers
 */
const fixed2 = (v: any): string => {
  const n = Number(String(v ?? "").replace(/[^0-9.-]+/g, ""));
  return (Number.isFinite(n) ? n : 0).toFixed(2);
};

const int0 = (v: any): number => {
  const n = Number(String(v ?? "").replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(n) ? Math.trunc(n) : 0;
};

const escapeHtml = (v: any): string =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatDate = (v: any): string => {
  if (!v) return "-";
  // si viene yyyy-mm-dd...
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);

  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("es-CO", { year: "numeric", month: "2-digit", day: "2-digit" });
};

const sectionTitle = (title: string) => `
  <h3 class="text-center text-sm font-bold text-blue-700 mt-3 mb-2 border-b pb-1">${escapeHtml(title)}</h3>
`;

/**
 * Normaliza nombres de campos por si backend varía
 */
const normalizeRow = (r: AnyRow): SedeStatusRow => ({
  sede: String(r.sede ?? r.nombre_sede ?? r.descripcion_sede ?? r.cod_sede ?? "N/A"),
  existencia: int0(r.existencia ?? r.stock ?? r.inv ?? r.disponible ?? 0),
  vmd: Number(r.vmd ?? r.movimiento ?? r.VMD ?? 0) || 0,
  ultima_compra_fecha: r.ultima_compra_fecha ?? r.fecha_ultima_compra ?? r.fec_ult_compra ?? null,
  ultima_compra_cantidad: int0(r.ultima_compra_cantidad ?? r.cantidad_ultima_compra ?? r.cant_ult_compra ?? 0),
  ultima_venta_fecha: r.ultima_venta_fecha ?? r.fecha_ultima_venta ?? r.fec_ult_venta ?? null,
  ultima_venta_cantidad: int0(r.ultima_venta_cantidad ?? r.cantidad_ultima_venta ?? r.cant_ult_venta ?? 0),
});

/**
 * API
 */
async function fetchSedesStatus(
  axios: any,
  apiRoute: string,
  params: { barra?: string; item?: string }
) {
  const token = localStorage.getItem("authToken") || "";

  // 🔧 AJUSTA ESTE ENDPOINT a tu API real
  return axios.get(`${apiRoute}/inventario/estado-sedes`, {
    headers: { Authorization: `Bearer ${token}` },
    params,
  });
}

/**
 * HTML builder
 */
function buildSedesHtml(rows: SedeStatusRow[], meta?: { barra?: string; item?: string }): string {
  const totalExistencia = rows.reduce((acc, r) => acc + (r.existencia || 0), 0);
  const totalUltCompra = rows.reduce((acc, r) => acc + (r.ultima_compra_cantidad || 0), 0);
  const totalUltVenta = rows.reduce((acc, r) => acc + (r.ultima_venta_cantidad || 0), 0);

  const headerInfo = `
    <div class="text-xs text-gray-600 flex flex-wrap gap-2 justify-center mb-2">
      ${meta?.barra ? `<span><b>Barra:</b> ${escapeHtml(meta.barra)}</span>` : ""}
    </div>
  `;

  const table = rows.length
    ? `
      <div class="max-h-[60vh] overflow-auto border border-gray-200 rounded">
        <table class="w-full text-sm border-collapse">
          <thead class="sticky top-0 bg-gray-100 z-10">
            <tr class="text-left">
              <th class="p-2 border-b">Sede</th>
              <th class="p-2 border-b text-right">Existencia</th>
              <th class="p-2 border-b text-right">VMD</th>
              <th class="p-2 border-b">Últ. compra (fecha)</th>
              <th class="p-2 border-b text-right">Últ. compra (cant.)</th>
              <th class="p-2 border-b">Últ. venta (fecha)</th>
              <th class="p-2 border-b text-right">Últ. venta (cant.)</th>
            </tr>
          </thead>
          <tbody>
            ${rows
              .map(
                (r) => `
              <tr class="hover:bg-gray-50">
                <td class="p-2 border-b">${escapeHtml(r.sede)}</td>
                <td class="p-2 border-b text-right font-semibold">${escapeHtml(r.existencia)}</td>
                <td class="p-2 border-b text-right">${escapeHtml(fixed2(r.vmd))}</td>
                <td class="p-2 border-b">${escapeHtml(formatDate(r.ultima_compra_fecha))}</td>
                <td class="p-2 border-b text-right">${escapeHtml(r.ultima_compra_cantidad)}</td>
                <td class="p-2 border-b">${escapeHtml(formatDate(r.ultima_venta_fecha))}</td>
                <td class="p-2 border-b text-right">${escapeHtml(r.ultima_venta_cantidad)}</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
          <tfoot class="bg-gray-50">
            <tr>
              <td class="p-2 font-bold">Totales</td>
              <td class="p-2 text-right font-bold">${escapeHtml(totalExistencia)}</td>
              <td class="p-2"></td>
              <td class="p-2"></td>
              <td class="p-2 text-right font-bold">${escapeHtml(totalUltCompra)}</td>
              <td class="p-2"></td>
              <td class="p-2 text-right font-bold">${escapeHtml(totalUltVenta)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    `
    : `
      <div class="text-sm text-gray-600 border border-gray-200 rounded p-3 text-center">
        No hay información de sedes para mostrar.
      </div>
    `;

  return `
    <div class="max-w-4xl mx-auto p-2 text-sm font-sans text-gray-800">
      ${headerInfo}
      <div class="bg-white rounded-md border p-3">
        ${sectionTitle("Estado del producto en sedes")}
        ${table}
      </div>
    </div>
  `;
}

/**
 * Modal
 */
export default async function openSedesStatusModal(
  axios: any,
  apiRoute: string,
  params: { barra?: string; item?: string }
) {
  try {
    // loader “seguro”
    Swal.fire({
      title: "Consultando sedes...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
      showConfirmButton: false,
    });

    const response = await fetchSedesStatus(axios, apiRoute, params);

    // soporta: array directo o {data: []}
    const raw = Array.isArray(response.data) ? response.data : response.data?.data ?? [];
    const rows = (raw as AnyRow[]).map(normalizeRow);

    const html = buildSedesHtml(rows, params);

    Swal.fire({
      title: "ESTADO EN SEDES",
      html,
      showCloseButton: true,
      showConfirmButton: false,
      focusConfirm: false,
      customClass: { popup: "swal2-popup swal2-modal swal2-show w-max" },
    });
  } catch (err) {
    console.log(err);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No se pudo cargar el estado del producto en sedes.",
      showCloseButton: true,
    });
  }
}